import { useState } from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Info } from "lucide-react";
import { i18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SectionCard } from "@/components/ui/section-card";
import { Stepper } from "./stepper";
import { RadioOption } from "./radio-option";
import type { Step2Form } from "@/constants/statement-constants";
import { getWcagOptions, getConformanceOptions } from "@/constants/statement-constants";

interface StatementStep2Props {
  step2: Step2Form;
  onChange: (updated: Step2Form) => void;
  onBack: () => void;
  onGenerate: () => void;
}

const PREDEFINED_WCAG = ["WCAG 2.2 Level AA", "WCAG 2.1 Level AA", "WCAG 2.0 Level AA"];

export function StatementStep2({ step2, onChange, onBack, onGenerate }: StatementStep2Props) {
  const isOtherWcag = !PREDEFINED_WCAG.includes(step2.wcagStandard);
  const radioWcagValue = isOtherWcag ? "Other" : step2.wcagStandard;

  const [otherWcagText, setOtherWcagText] = useState(
    isOtherWcag && step2.wcagStandard !== "Other" ? step2.wcagStandard : ""
  );

  const handleWcagRadioChange = (v: string) => {
    if (v === "Other") {
      onChange({ ...step2, wcagStandard: otherWcagText || "Other" });
    } else {
      onChange({ ...step2, wcagStandard: v });
    }
  };

  const handleOtherWcagInput = (text: string) => {
    setOtherWcagText(text);
    onChange({ ...step2, wcagStandard: text || "Other" });
  };

  return (
    <SectionCard className="cy:border cy:border-gray-200">
      <h3 className="cy:text-[18px] cy:font-semibold cy:text-foreground cy:mb-1">
        {i18n.__( 'Generate statement', 'accessibility-widget' )}
      </h3>
      <hr className="cy:border-gray-200 cy:mb-6" />

      <div className="cy:max-w-lg">
        <Stepper step={2} />
      </div>

      <div className="cy:space-y-6 cy:max-w-lg">
        <div className="cy:mb-2">
          <h4 className="cy:text-[18px] cy:font-semibold cy:text-foreground">{i18n.__( 'Conformance status', 'accessibility-widget' )}</h4>
        </div>

        {/* WCAG Standard */}
        <div className="cy:space-y-3">
          <p className="cy:text-[16px] cy:font-medium cy:text-foreground">
            {i18n.__( '1. Select the accessibility standards you follow.', 'accessibility-widget' )}
            <span className="cy:text-red-500 cy:ml-0.5">*</span>
          </p>
          <RadioGroupPrimitive.Root
            value={radioWcagValue}
            onValueChange={handleWcagRadioChange}
            className="cy:space-y-2.5 cy:text-[16px] cy:text-black-600"
          >
            {getWcagOptions().map((o) => (
              <RadioOption
                key={o.value}
                value={o.value}
                label={o.label}
                checked={radioWcagValue === o.value}
              />
            ))}
          </RadioGroupPrimitive.Root>

          {/* Custom input shown when "Other" is selected */}
          {isOtherWcag && (
            <div className="cy:ml-7 cy:mt-2">
              <Input
                value={otherWcagText}
                onChange={(e) => handleOtherWcagInput(e.target.value)}
                placeholder={i18n.__( 'Please specify the standard', 'accessibility-widget' )}
                className="cy:h-[40px] cy:border! cy:rounded! cy:max-w-xs"
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Conformance Status */}
        <div className="cy:space-y-3">
          <p className="cy:text-[16px] cy:font-medium cy:text-foreground">
            {i18n.__( '2. Conformance status', 'accessibility-widget' )}<span className="cy:text-red-500 cy:ml-0.5">*</span>
          </p>
          <RadioGroupPrimitive.Root
            value={step2.conformanceStatus}
            onValueChange={(v) => onChange({ ...step2, conformanceStatus: v })}
            className="cy:space-y-3 cy:text-[16px] cy:text-black-600"
          >
            {getConformanceOptions().map((o) => (
              <RadioOption
                key={o.value}
                value={o.value}
                label={o.label}
                description={o.description}
                checked={step2.conformanceStatus === o.value}
              />
            ))}
          </RadioGroupPrimitive.Root>
        </div>

      </div>

      {/* Info banner — full width, outside max-w-lg */}
      <div className="cy:mt-6 cy:flex cy:gap-3 cy:rounded-lg cy:border cy:border-blue-200 cy:bg-blue-50 cy:p-4">
        <Info className="cy:w-4 cy:h-4 cy:text-blue-600 cy:flex-shrink-0 cy:mt-0.5" />
        <p className="cy:text-sm cy:tex-black-600 cy:leading-relaxed">
          {i18n.__( 'The statement will be generated based on the information you provide and your own assessment. Please ensure that the details entered accurately reflect your website\'s accessibility status and applicable requirements.', 'accessibility-widget' )}
        </p>
      </div>

      <hr className="cy:border-gray-200 cy:mt-6" />
      {/* Buttons: Back left, Generate statement right */}
      <div className="cy:mt-6 cy:flex cy:justify-between">
        <Button
          variant="outline"
          onClick={onBack}
          className="cy:rounded cy:border-primary! cy:text-primary!"
        >
          {i18n.__( 'Back', 'accessibility-widget' )}
        </Button>
        <Button onClick={onGenerate} className="cy:rounded">
          {i18n.__( 'Generate statement', 'accessibility-widget' )}
        </Button>
      </div>
    </SectionCard>
  );
}
